import { FC, useEffect, useState, type ChangeEvent } from 'react';
import { Box, TextField } from '@mui/material';
import StyledLabel from 'modules/map/components/daily-dish/components/utils/StyledLabel';
import { FIELD_WIDTHS } from 'modules/map/components/daily-dish/constants';
import {
  createNumberFieldState,
  numberFieldBlur,
  numberFieldInput,
  numberFieldSync,
} from 'modules/map/components/daily-dish/components/form/numberFieldRules';

interface NumberFieldWithLimitsProps {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
  width?: string;
}

/**
 * Reusable number input field with validation
 *
 * Ô nhập giữ riêng chuỗi người dùng đang gõ (draft) thay vì gắn thẳng với giá trị
 * đã áp dụng — xem numberFieldRules để biết lý do.
 */
export const NumberFieldWithLimits: FC<NumberFieldWithLimitsProps> = ({
  label,
  value,
  min,
  max,
  onChange,
  width = FIELD_WIDTHS.HALF,
}) => {
  const [state, setState] = useState(() => createNumberFieldState(value));

  // Giá trị đổi từ bên ngoài (xoay preset, reset, chọn mẫu...) → ô nhập theo kịp.
  useEffect(() => {
    setState((prev) => numberFieldSync(prev, value));
  }, [value]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const result = numberFieldInput(e.target.value, min, max);
    setState(result.state);
    if (result.applied !== null) onChange(result.applied);
  };

  const handleBlur = () => {
    setState((prev) => numberFieldBlur(prev, min, max));
  };

  return (
    <Box sx={{ width }}>
      <StyledLabel label={label} />
      <TextField
        type="number"
        fullWidth
        size="small"
        value={state.draft}
        error={state.error !== null}
        helperText={state.error ?? undefined}
        inputProps={{ min, max, step: 1, 'aria-label': label }}
        onChange={handleChange}
        onBlur={handleBlur}
      />
    </Box>
  );
};
